/**
 * `useMaintenant` — l'instant présent, qui avance. Le seul hook d'horloge de la famille : tout ce
 * qui bouge avec le temps (le trait de maintenant, un compte à rebours, « il y a 3 min ») le lit.
 *
 *   const maintenant = useMaintenant(60_000);        // une fois par minute, calée sur la minute ronde
 *   const maintenant = useMaintenant(1_000, fige);   // `fige` (un nombre) gagne : la démo, les tests, l'app qui a déjà son tic
 *
 * UNE minuterie par rythme pour toute la page, quel que soit le nombre d'abonnés : une table de
 * deux cents « il y a 3 min » ne pose pas deux cents minuteries. Calée sur le multiple du rythme
 * (une minute tourne à :00, pas à :37) : deux horloges d'un même écran changent ensemble. Onglet
 * caché : on rattrape au retour.
 */
import { useCallback, useSyncExternalStore } from "react";

interface Horloge {
  maintenant: number;
  abonnes: Set<() => void>;
  minuterie?: number;
}

const horloges = new Map<number, Horloge>();

function horlogeDe(rythme: number): Horloge {
  let h = horloges.get(rythme);
  if (!h) {
    h = { maintenant: Date.now(), abonnes: new Set() };
    horloges.set(rythme, h);
  }
  return h;
}

function tic(rythme: number) {
  const h = horlogeDe(rythme);
  window.clearTimeout(h.minuterie);
  h.maintenant = Date.now();
  for (const prevenir of h.abonnes) prevenir();
  h.minuterie = window.setTimeout(() => tic(rythme), rythme - (h.maintenant % rythme) + 5);
}

function auRetour() {
  if (document.visibilityState !== "visible") return;
  for (const [rythme, h] of horloges) if (h.abonnes.size > 0) tic(rythme);
}

function abonner(rythme: number, prevenir: () => void): () => void {
  const h = horlogeDe(rythme);
  h.abonnes.add(prevenir);
  if (h.abonnes.size === 1) {
    tic(rythme);
    if ([...horloges.values()].filter((x) => x.abonnes.size > 0).length === 1) {
      document.addEventListener("visibilitychange", auRetour);
    }
  }
  return () => {
    h.abonnes.delete(prevenir);
    if (h.abonnes.size === 0) {
      window.clearTimeout(h.minuterie);
      h.minuterie = undefined;
      if ([...horloges.values()].every((x) => x.abonnes.size === 0)) {
        document.removeEventListener("visibilitychange", auRetour);
      }
    }
  };
}

const rien = () => () => {};

export function useMaintenant(rythmeMs = 60_000, fige?: number | null): number {
  const estFige = fige !== undefined && fige !== null;
  const rythme = Math.max(250, rythmeMs);
  const sAbonner = useCallback((prevenir: () => void) => abonner(rythme, prevenir), [rythme]);
  const lire = useCallback(() => horlogeDe(rythme).maintenant, [rythme]);
  const maintenant = useSyncExternalStore(estFige ? rien : sAbonner, lire, lire);
  return estFige ? fige : maintenant;
}
